import React, {
  useState,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { FieldType } from 'utils/enum';
import { Layout } from 'components/Form';
import { useBidTypeChoices, useRepChoices } from 'queries/company';
import { useJobSupplierChoices } from 'queries/job';
import useAuth from 'hooks/useAuth';
import { processSchema } from 'utils/form';

export interface IFormValues {
  rep: number;
  bid_type: string;
  supplier: number;
  supplier_location?: number;
  srs_job_account?: number;
}

export interface IProcessedFormData {
  rep: number;
  bid_type: string;
  supplier: number;
  supplier_location?: number;
  srs_job_account?:
    | {
        jobAccountNumber: any;
        jobAccountName: any;
      }
    | undefined;
}

export type EstimateFormRef = {
  getFormData: () => IProcessedFormData | null;
  validate: () => boolean;
  checkValidity: () => boolean;
  validateSupplierFields: () => boolean;
  getSrsJobAccountPayload: () =>
    | { jobAccountNumber: any; jobAccountName: any }
    | undefined;
  handleSubmit: (
    onSubmit: (data: IFormValues) => void
  ) => (e?: React.BaseSyntheticEvent) => Promise<void>;
  reset: (values?: Partial<IFormValues>) => void;
  setValue: (name: keyof IFormValues, value: any) => void;
};

type FormProps = {
  jobId: string;
  loading?: boolean;
  showRepField?: boolean;
  showBidTypeField?: boolean;
  onRepSelect?: (rep: number) => void;
  onBidTypeSelect?: (bidType: string) => void;
  onFormChange?: () => void;
  // For manual mode - config-based schema processing
  config?: any;
  defaultValues?: Partial<IFormValues>;
  // For auto mode - shared values syncing
  sharedRep?: number;
  sharedBidType?: string;
  initialRep?: number;
  initialBidType?: string;
};

const EstimateForm = forwardRef<EstimateFormRef, FormProps>(
  (
    {
      jobId,
      loading = false,
      showRepField = true,
      showBidTypeField = true,
      onRepSelect,
      onBidTypeSelect,
      onFormChange,
      config,
      defaultValues,
      sharedRep,
      sharedBidType,
      initialRep,
      initialBidType,
    },
    ref
  ) => {
    const { userInfo } = useAuth();
    const [supplier, setSupplier] = useState<any>(null);
    const [supplierLocation, setSupplierLocation] = useState<any>(null);
    const [srsJobAccount, setSrsJobAccount] = useState<any>(null);
    const isUpdatingRepFromSharedRef = useRef(false);
    const isUpdatingBidTypeFromSharedRef = useRef(false);

    // Create schema - use processSchema if config is provided, otherwise use simple optional schema
    const schema = useMemo(() => {
      const baseSchema = {
        rep: yup.number(),
        bid_type: yup.string(),
        supplier: yup.number().optional(),
        supplier_location: yup.number().nullable().optional(),
        srs_job_account: yup.number().nullable().optional(),
      };

      if (config) {
        return yup.object(
          processSchema(baseSchema, config, [
            'supplier_location',
            'srs_job_account',
          ])
        );
      }

      // Simple all-optional schema for auto mode
      return yup.object({
        rep: yup.number().optional(),
        bid_type: yup.string().optional(),
        supplier: yup.number().optional(),
        supplier_location: yup.number().nullable().optional(),
        srs_job_account: yup.number().nullable().optional(),
      });
    }, [config]);

    const {
      control,
      formState: { errors },
      setValue,
      getValues,
      watch,
      handleSubmit,
      reset,
      setError,
    } = useForm<IFormValues>({
      resolver: yupResolver(schema),
      defaultValues: defaultValues || {
        rep: initialRep || sharedRep,
        bid_type: initialBidType || sharedBidType,
      },
    });

    // Watch for rep and bid_type changes
    const repValue = watch('rep');
    const bidTypeValue = watch('bid_type');

    // Notify parent when form values change
    useEffect(() => {
      onFormChange?.();
    }, [repValue, bidTypeValue, onFormChange]);

    // Sync from sharedRep to form (for auto mode)
    useEffect(() => {
      if (isUpdatingRepFromSharedRef.current) {
        isUpdatingRepFromSharedRef.current = false;
        return;
      }

      if (sharedRep && (!repValue || repValue !== sharedRep)) {
        isUpdatingRepFromSharedRef.current = true;
        setValue('rep', sharedRep, { shouldDirty: false });
      }
    }, [sharedRep, repValue, setValue]);

    // Sync from sharedBidType to form (for auto mode)
    useEffect(() => {
      if (isUpdatingBidTypeFromSharedRef.current) {
        isUpdatingBidTypeFromSharedRef.current = false;
        return;
      }

      if (sharedBidType && (!bidTypeValue || bidTypeValue !== sharedBidType)) {
        isUpdatingBidTypeFromSharedRef.current = true;
        setValue('bid_type', sharedBidType, { shouldDirty: false });
      }
    }, [sharedBidType, bidTypeValue, setValue]);

    // Initialize form with initial values (for auto mode)
    useEffect(() => {
      if (initialRep && !repValue) {
        setValue('rep', initialRep);
      }
      if (initialBidType && !bidTypeValue) {
        setValue('bid_type', initialBidType);
      }
    }, [initialRep, initialBidType, setValue, repValue, bidTypeValue]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      getFormData: () => {
        const values = getValues();
        // Use shared values if form doesn't have them (auto mode)
        const finalRep = values.rep || sharedRep || initialRep;
        const finalBidType = values.bid_type || sharedBidType || initialBidType;

        if (!finalRep || !finalBidType) {
          if (!finalRep && showRepField) {
            setError('rep', { message: 'Please select a rep' });
          }
          if (!finalBidType && showBidTypeField) {
            setError('bid_type', { message: 'Please select a bid type' });
          }
          return null;
        }

        // Validate supplier fields
        if (supplier?.integrated) {
          if (!supplierLocation) {
            setError('supplier_location', {
              message: 'Please select a supplier location',
            });
            return null;
          }
          if (supplier.supplier_code === 'SRS_SUPPLY' && !srsJobAccount) {
            setError('srs_job_account', {
              message: 'Please select a SRS job account',
            });
            return null;
          }
        }

        return {
          ...values,
          rep: finalRep,
          bid_type: finalBidType,
          srs_job_account: srsJobAccount
            ? {
                jobAccountNumber: srsJobAccount['value'],
                jobAccountName: srsJobAccount['name'],
              }
            : undefined,
        };
      },
      validate: () => {
        const values = getValues();
        const finalRep = values.rep || sharedRep || initialRep;
        const finalBidType = values.bid_type || sharedBidType || initialBidType;
        let isValid = true;

        if (!finalRep && showRepField) {
          setError('rep', { message: 'Please select a rep' });
          isValid = false;
        }

        if (!finalBidType && showBidTypeField) {
          setError('bid_type', { message: 'Please select a bid type' });
          isValid = false;
        }

        if (supplier?.integrated) {
          if (!supplierLocation) {
            setError('supplier_location', {
              message: 'Please select a supplier location',
            });
            isValid = false;
          }
          if (supplier.supplier_code === 'SRS_SUPPLY' && !srsJobAccount) {
            setError('srs_job_account', {
              message: 'Please select a SRS job account',
            });
            isValid = false;
          }
        }

        return isValid;
      },
      checkValidity: () => {
        const values = getValues();
        const finalRep = values.rep || sharedRep || initialRep;
        const finalBidType = values.bid_type || sharedBidType || initialBidType;

        if (!finalRep && showRepField) return false;
        if (!finalBidType && showBidTypeField) return false;
        if (supplier?.integrated && !supplierLocation) return false;
        if (
          supplier?.integrated &&
          supplier.supplier_code === 'SRS_SUPPLY' &&
          !srsJobAccount
        )
          return false;

        return true;
      },
      validateSupplierFields: () => {
        if (supplier?.integrated) {
          if (!supplierLocation) {
            setError('supplier_location', {
              message: 'Please select a supplier location',
            });
            return false;
          }
          if (supplier.supplier_code === 'SRS_SUPPLY' && !srsJobAccount) {
            setError('srs_job_account', {
              message: 'Please select a SRS job account',
            });
            return false;
          }
        }
        return true;
      },
      getSrsJobAccountPayload: () => {
        if (srsJobAccount) {
          return {
            jobAccountNumber: srsJobAccount['value'],
            jobAccountName: srsJobAccount['name'],
          };
        }
        return undefined;
      },
      handleSubmit,
      reset,
      setValue,
    }));

    const fields = useMemo(() => {
      const _fields: any[] = [];

      if (showRepField) {
        _fields.push({
          name: 'rep',
          type: FieldType.AsyncSelect,
          label: 'Select Rep',
          placeholder: 'Select rep',
          error: errors.rep?.message,
          control,
          optionsPromise: useRepChoices,
          forceDropdown: true,
          postSelect: (selected: any) => {
            if (selected?.value && onRepSelect) {
              onRepSelect(selected.value);
            }
          },
        });
      }

      if (showBidTypeField) {
        _fields.push({
          name: 'bid_type',
          type: FieldType.AsyncSelect,
          label: 'Select Bid Type',
          placeholder: 'Select Bid Type',
          error: errors.bid_type?.message,
          control,
          optionsPromise: useBidTypeChoices,
          orderByLabel: false,
          forceDropdown: true,
          postSelect: (selected: any) => {
            if (selected?.value && onBidTypeSelect) {
              onBidTypeSelect(selected.value);
            }
          },
        });
      }

      if (userInfo?.integrations?.srs) {
        _fields.push({
          name: 'supplier',
          type: FieldType.AsyncSelect,
          label: 'Select Supplier',
          placeholder: 'Select Supplier',
          error: errors.supplier?.message,
          orderByLabel: false,
          forceDropdown: true,
          control,
          // eslint-disable-next-line react-hooks/rules-of-hooks
          optionsPromise: () => useJobSupplierChoices(jobId),
          postSelect: (selected: any) => {
            setSupplier(selected);
            if (selected.integrated && selected.locations?.length > 0) {
              const selectedLocation = selected.locations[0];
              setSupplierLocation(selectedLocation);
              setValue('supplier_location', selectedLocation?.value);
              if (selectedLocation.job_accounts?.length > 0) {
                const selectedJobAccount = selectedLocation.job_accounts[0];
                setSrsJobAccount(selectedJobAccount);
                setValue('srs_job_account', selectedJobAccount?.value);
              }
            } else {
              setSupplierLocation(null);
              setSrsJobAccount(null);
              setValue('supplier_location', undefined);
              setValue('srs_job_account', undefined);
            }
          },
        });
      }

      if (supplier?.integrated && supplier?.locations?.length > 1) {
        _fields.push({
          name: 'supplier_location',
          type: FieldType.Select,
          label: 'Select Supplier Location',
          options: supplier.locations,
          error: errors.supplier_location?.message,
          control,
          postSelect: (selected: any) => {
            setSupplierLocation(selected);
            if (selected.job_accounts?.length > 0) {
              const selectedJobAccount = selected.job_accounts[0];
              setSrsJobAccount(selectedJobAccount);
              setValue('srs_job_account', selectedJobAccount?.value);
            } else {
              setSrsJobAccount(null);
              setValue('srs_job_account', undefined);
            }
          },
        });
      }

      if (supplier?.integrated && supplierLocation?.job_accounts?.length > 1) {
        _fields.push({
          name: 'srs_job_account',
          type: FieldType.Select,
          label: 'Select SRS Job Account',
          options: supplierLocation.job_accounts,
          error: errors.srs_job_account?.message,
          control,
          postSelect: (selected: any) => {
            setSrsJobAccount(selected);
          },
        });
      }

      return _fields;
    }, [
      errors,
      control,
      userInfo,
      supplier,
      supplierLocation,
      srsJobAccount,
      jobId,
      setValue,
      showRepField,
      showBidTypeField,
      onRepSelect,
      onBidTypeSelect,
    ]);

    // Auto-select single supplier location
    useEffect(() => {
      if (supplier?.integrated && supplier?.locations?.length === 1) {
        setSupplierLocation(supplier.locations[0]);
        setValue('supplier_location', supplier.locations[0]?.value);
      }
    }, [supplier, setValue]);

    // Auto-select single SRS job account
    useEffect(() => {
      if (
        supplier?.integrated &&
        supplierLocation?.job_accounts?.length === 1
      ) {
        setSrsJobAccount(supplierLocation.job_accounts[0]);
        setValue('srs_job_account', supplierLocation.job_accounts[0]?.value);
      }
    }, [supplierLocation, supplier, setValue]);

    return <Layout fields={fields} loading={loading} />;
  }
);

EstimateForm.displayName = 'EstimateForm';

export default EstimateForm;
